import { type FC, type PropsWithChildren, useMemo } from 'react'

import { useAutomatedMessageContext } from '@liveHelper/components/AutomatedMessageProvider'
import { useMessagePreviewContext } from '@liveHelper/components/MessagePreviewProvider'
import { OneToOneModalRenderer } from '@liveHelper/components/OneToOneModalRenderer'
import { OnlineIndicator as AgentOnlineIndicator } from '@liveHelper/components/shared/styles'
import { WidgetMessagePreview } from '@liveHelper/components/shared/WidgetContainer/WidgetMessagePreview'
import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import { useDraggableContext } from '@liveHelper/context/DraggableContext'
import { useIsOffline } from '@liveHelper/hooks/useIsOffline'
import { useTopLevelUIState } from '@liveHelper/hooks/useTopLevelUIState'
import { useWidgetPosition } from '@liveHelper/hooks/useWidgetPosition'
import {
  paramsSelector,
  queuePositionSelector,
  uiStateSelector,
  useStore,
  waitlistCapacityReachedSelector,
} from '@liveHelper/models/store'
import { LIVE_HELPER_WIDGET_CONTAINER } from '@liveHelper/testIds'
import { useTrackEmbedImpression } from '@liveHelper/tracking/useLiveHelperTracking'
import ScrollFreeze from '@oneToOne/shared/components/ScrollFreeze'
import { useReconnectClosedSocket } from '@oneToOne/shared/contexts/useReconnectClosedSocket'

import { FocusTrap } from '@core/components/FocusTrap'
import { ImpressionContainer } from '@core/components/ImpressionContainer'
import { BreakpointContext } from '@core/components/Layout/Breakpoint/BreakpointContext'
import { useMeasure } from '@core/hooks/useMeasure'
import { ModalContainerContext } from '@core/modal/modalContainer'
import { isMobileWeb } from '@core/userAgent'

import {
  LiveHelperModal,
  TransitionContainer,
  WIDGET_SIZES,
} from './WidgetContainer.styles'

/**
 * Determines if the widget is in fullscreen mode.
 */
export const useIsFullScreen = () => {
  const uiState = useStore(uiStateSelector)

  const topLevelUIState = useTopLevelUIState()
  const isMobileWebEnabled = isMobileWeb()

  const isIdleMobile =
    isMobileWebEnabled &&
    (uiState === 'idle_expanded' ||
      uiState === 'idle_scheduling' ||
      uiState === 'request_visitor_information' ||
      uiState === 'request_microphone_access' ||
      uiState === 'appointment_lobby' ||
      uiState === 'appointment_dialing')
  const isDialingMobile = isMobileWebEnabled && uiState === 'dialing_expanded'
  const isChattingMobile = isMobileWebEnabled && topLevelUIState === 'chat'
  const isFullScreen =
    uiState === 'on_call_fullscreen' ||
    isChattingMobile ||
    isDialingMobile ||
    isIdleMobile

  return isFullScreen
}

/**
 * Calculates the container height based on the current UI state and environment.
 */
export const useTransitionContainerHeight = () => {
  const uiState = useStore(uiStateSelector)
  const queuePosition = useStore(queuePositionSelector)

  const topLevelUIState = useTopLevelUIState()
  const { lastUnreadMessage } = useMessagePreviewContext()
  const { automatedMessage } = useAutomatedMessageContext()
  const isMobileWebEnabled = isMobileWeb()
  const isFullScreen = useIsFullScreen()
  const chatChannelConfig = useChatChannelConfig()
  const waitlistCapacityReached = useStore(waitlistCapacityReachedSelector)
  const isAvatarIdleUI = Boolean(
    chatChannelConfig?.widget_style?.icon_style === 'avatar',
  )
  const isLargeAvatar = Boolean(
    chatChannelConfig?.widget_style?.avatar_size === 'large',
  )
  const isOfflineWidgetEnabled = useIsOffline()

  switch (true) {
    case uiState === 'idle_expanded':
      if (isOfflineWidgetEnabled || waitlistCapacityReached || isFullScreen) {
        return 'auto'
      }

      return WIDGET_SIZES.expanded.default.height
    case uiState === 'idle_minimized' &&
      !!(
        lastUnreadMessage ||
        automatedMessage ||
        (isAvatarIdleUI && chatChannelConfig?.widget_style?.idle_state_text)
      ) &&
      !queuePosition:
      return isLargeAvatar
        ? WIDGET_SIZES.minimized.large_avatar.height
        : WIDGET_SIZES.minimized.avatar.height
    case uiState === 'dialing_minimized':
      return WIDGET_SIZES.minimized.default.height
    case uiState === 'request_microphone_access':
    case uiState === 'request_visitor_information':
      if (isFullScreen) {
        return 'auto'
      }

      return WIDGET_SIZES.expanded.default.height
    case uiState === 'idle_scheduling':
      if (isFullScreen) {
        return WIDGET_SIZES.fullscreen.default.height
      }

      return WIDGET_SIZES.expanded.default.height
    case uiState === 'dialing_expanded' && !isMobileWebEnabled:
    case uiState === 'on_call_expanded':
    case uiState === 'appointment_lobby' && !isMobileWebEnabled:
    case uiState === 'appointment_dialing' && !isMobileWebEnabled:
      return WIDGET_SIZES.expanded.default.height
    case isFullScreen && isMobileWebEnabled:
      return WIDGET_SIZES.fullscreen.mobile.height
    case isFullScreen && !isMobileWebEnabled:
      return WIDGET_SIZES.fullscreen.default.height // [OTO-1379] This is a hack to fix the fullscreen issue on safari desktop
    case topLevelUIState === 'minimized':
      return WIDGET_SIZES.minimized.default.height
    case topLevelUIState === 'chat':
      return WIDGET_SIZES.expanded.default.height
    default:
      return 'inherit'
  }
}

/**
 * WidgetContainer is the wrapper for the client widget.
 * It is in charge of managing its size (width & height), border radius, box
 * shadow and position based on UiState.
 */
export const WidgetContainer: FC<PropsWithChildren> = ({ children }) => {
  const {
    ref, // TODO(OTO-172): Optimize this to avoid unnecessary re-rendering
    rect: { height, width },
  } = useMeasure<HTMLDivElement>()
  const chatChannelConfig = useChatChannelConfig()

  const params = useStore(paramsSelector)
  const uiState = useStore(uiStateSelector)
  const trackEmbedImpression = useTrackEmbedImpression()
  const { showWidgetOnLeftSide } = useWidgetPosition()
  const queuePosition = useStore(queuePositionSelector)

  const isMobileWebEnabled = isMobileWeb()
  const isOffline = useIsOffline()

  const topLevelUIState = useTopLevelUIState()
  const isFullScreen = useIsFullScreen()
  const isMinimized = topLevelUIState === 'minimized'
  const zIndex =
    uiState === 'idle_minimized' && params.idle_minimized_z_index
      ? params.idle_minimized_z_index
      : undefined

  const { lastUnreadMessage } = useMessagePreviewContext()
  const { automatedMessage } = useAutomatedMessageContext()
  const isAvatarIdleUI = Boolean(
    chatChannelConfig?.widget_style?.icon_style === 'avatar',
  )
  const isLargeAvatar = Boolean(
    chatChannelConfig?.widget_style?.avatar_size === 'large',
  )

  const transitionContainerHeight = useTransitionContainerHeight()

  const reconnectClosedSocket = useReconnectClosedSocket()

  const renderOnlineIndicator = useMemo(() => {
    if (!isOffline) {
      return (
        <AgentOnlineIndicator
          hasLastUnreadMessage={!!lastUnreadMessage || !!automatedMessage}
        />
      )
    }
  }, [automatedMessage, isOffline, lastUnreadMessage])

  const activateFocusTrap =
    uiState === 'on_call_fullscreen' ||
    (isMobileWebEnabled && uiState === 'dialing_expanded') ||
    (isMobileWebEnabled && topLevelUIState === 'chat')

  const draggableContext = useDraggableContext()
  const draggableProperties = draggableContext
    ? {
        ...draggableContext.attributes,
        ref: draggableContext.setNodeRef,
        style: draggableContext.style,
      }
    : {}

  return (
    <ModalContainerContext.Provider value={ref.current}>
      {ref.current ? <OneToOneModalRenderer /> : null}
      <ScrollFreeze enabled={isFullScreen}>
        <FocusTrap disabled={!activateFocusTrap} fullHeight={false}>
          {isMinimized ? (
            <WidgetMessagePreview
              modalHeight={ref.current?.getBoundingClientRect?.().height}
            />
          ) : null}
          <LiveHelperModal
            {...draggableProperties}
            // Important to disable Chrome's translate feature
            // which is breaking our UI by injecting spans and
            // duplicating content. This is partially a Preact,
            // however React does have its own problems with
            // dynamic translations. (CS-1430)
            uiState={uiState}
            className="notranslate"
            isPositionedLeft={showWidgetOnLeftSide}
            isFullscreen={isFullScreen}
            hasFloatingMessage={!!lastUnreadMessage || !!automatedMessage}
            isAvatarIdleUI={isAvatarIdleUI}
            zIndex={zIndex}
          >
            <ImpressionContainer trackingFn={trackEmbedImpression}>
              <BreakpointContext.Provider
                value={{
                  height: height ?? 0,
                  width: width ?? 0,
                  provider: 'OneToOne Widget Container',
                }}
              >
                {uiState === 'idle_minimized' &&
                !isAvatarIdleUI &&
                !queuePosition
                  ? renderOnlineIndicator
                  : null}
                <TransitionContainer
                  ref={ref}
                  transitionContainerHeight={transitionContainerHeight}
                  topLevelUIState={topLevelUIState}
                  uiState={uiState}
                  isFullscreen={isFullScreen}
                  isMobileWebEnabled={isMobileWebEnabled}
                  hasLastUnreadMessage={
                    !!lastUnreadMessage || !!automatedMessage
                  }
                  hasQueuePosition={!!queuePosition}
                  isAvatarIdleUI={isAvatarIdleUI}
                  isLargeAvatar={isLargeAvatar}
                  data-testid={LIVE_HELPER_WIDGET_CONTAINER}
                  onClick={reconnectClosedSocket}
                >
                  {children}
                </TransitionContainer>
              </BreakpointContext.Provider>
            </ImpressionContainer>
          </LiveHelperModal>
        </FocusTrap>
      </ScrollFreeze>
    </ModalContainerContext.Provider>
  )
}
